import { useApi } from '../composables/useApi'

const api = useApi()

export { api }

export const dashboardApi = {
  getSalesOverview: async (period) => {
    try {
      const response = await api.get(`/dashboard/sales-overview?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales overview:', error);
      throw error;
    }
  },
  getOrdersOverview: async (period) => {
    try {
      const response = await api.get(`/dashboard/orders-overview?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching orders overview:', error);
      throw error;
    }
  },
  getOrderAmountsOverview: async (period) => {
    try {
      const response = await api.get(`/dashboard/order-amounts-overview?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order amounts overview:', error);
      throw error;
    }
  },
  getOrderStatusOverTime: async (period) => {
    try {
      const response = await api.get(`/dashboard/order-status-over-time?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order status over time:', error);
      throw error;
    }
  },
  getTopClients: async () => {
    try {
      const response = await api.get('/dashboard/top-clients');
      return response.data;
    } catch (error) {
      console.error('Error fetching top clients:', error);
      throw error;
    }
  },
  getTopProducts: async () => {
    try {
      const response = await api.get('/dashboard/top-products');
      return response.data;
    } catch (error) {
      console.error('Error fetching top products:', error);
      throw error;
    }
  },
  getActiveOrders: async () => {
    try {
      const response = await api.get('/dashboard/active-orders');
      return response.data;
    } catch (error) {
      console.error('Error fetching active orders:', error);
      throw error;
    }
  },
  getRecentPayments: async () => {
    try {
      const response = await api.get('/payments', {
        params: {
          limit: 5,
          sort: '-payment_date',
          with: ['order', 'order.client'],
          include: ['order', 'order.client'],
          load: ['order', 'order.client']
        }
      });
      console.log('Raw payments response:', response); // Debug log
      return response;
    } catch (error) {
      console.error('Error fetching recent payments:', error);
      throw error;
    }
  },
  getRecentExpenses: async () => {
    try {
      const response = await api.get('/dashboard/recent-expenses');
      return response.data;
    } catch (error) {
      console.error('Error fetching recent expenses:', error);
      throw error;
    }
  },
  getPendingOrders: async () => {
    try {
      const response = await api.get('/dashboard/pending-orders');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      throw error;
    }
  },
  getUpcomingDeadlines: async () => {
    try {
      const response = await api.get('/dashboard/upcoming-deadlines');
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming deadlines:', error);
      throw error;
    }
  },
  getLowStockItems: async () => {
    try {
      const response = await api.get('/dashboard/low-stock');
      return response.data;
    } catch (error) {
      console.error('Error fetching low stock items:', error);
      throw error;
    }
  },
  getStats: async () => {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },
  getOrderStatusOverview: async (period) => {
    try {
      const response = await api.get(`/dashboard/order-status-overview${period ? `?period=${period}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order status overview:', error);
      throw error;
    }
  },
  getRecentTransactions: async () => {
    try {
      const response = await api.get('/dashboard/transactions');
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },
  getTransactions: () => api.get('/dashboard/transactions'),
  getPendingApprovals: async () => {
    try {
      const response = await api.get('/dashboard/pending-approvals');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  },
  getPendingOrderApprovals: async () => {
    try {
      const response = await api.get('/orders/pending', { params: { limit: 5 } });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending order approvals:', error);
      throw error;
    }
  },
  getPendingPaymentApprovals: async () => {
    try {
      const response = await api.get('/payments/pending', { params: { limit: 5 } });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending payment approvals:', error);
      throw error;
    }
  },
  getPendingQuotationApprovals: async () => {
    try {
      const response = await api.get('/quotations/pending', { params: { limit: 5 } });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending quotation approvals:', error);
      throw error;
    }
  },
  getPendingExpenseApprovals: async () => {
    try {
      // Use exact same approach as ExpenseApprovals.vue
      const response = await api.get('/expenses/pending');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending expense approvals:', error);
      throw error;
    }
  },
  getSalesDashboard: async (period = 'month') => {
    try {
      const response = await api.get(`/dashboard/sales?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales dashboard:', error);
      throw error;
    }
  },
  getCriticalMetrics: async () => {
    try {
      const response = await api.get('/dashboard/critical-metrics');
      return response.data;
    } catch (error) {
      console.error('Error fetching critical metrics:', error);
      throw error;
    }
  },
  getLostClients: async () => {
    try {
      const response = await api.get('/dashboard/lost-clients');
      return response.data;
    } catch (error) {
      console.error('Error fetching lost clients:', error);
      throw error;
    }
  },
  getProductsStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.has_dimensions !== undefined) params.append('has_dimensions', filters.has_dimensions);
      const response = await api.get(`/products/stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products stats:', error);
      throw error;
    }
  }
}

export const ordersApi = {
  getAll: async (params = {}) => {
    try {
      console.log('API getAll original params:', params);
      
      // Ensure we include all necessary relations
      const enhancedParams = {
        ...params,
        with: Array.isArray(params.with) ? 
          [...new Set([...params.with, 'client', 'department', 'created_by_user', 'assigned_to_user', 'items'])] : 
          ['client', 'department', 'created_by_user', 'assigned_to_user', 'items'],
        include: Array.isArray(params.include) ? 
          [...new Set([...params.include, 'client', 'department', 'created_by_user', 'assigned_to_user', 'items'])] : 
          ['client', 'department', 'created_by_user', 'assigned_to_user', 'items'],
        // Add any additional parameters needed for proper relation loading
        load: ['client', 'department', 'created_by_user', 'assigned_to_user', 'items'],
        // Ensure we get the client classification field
        fields: {
          client: ['id', 'first_name', 'last_name', 'company_name', 'classification', 'email', 'phone']
        }
      };
      
      console.log('API getAll enhanced params:', enhancedParams);
      const response = await api.get('/orders', { 
        params: enhancedParams,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Log a sample of the response to see what's being returned
      if (response && response.data && response.data.length > 0) {
        const sampleOrder = response.data[0];
        console.log('API getAll sample order:', {
          id: sampleOrder.id,
          order_number: sampleOrder.order_number,
          assigned_to: sampleOrder.assigned_to,
          assigned_to_user: sampleOrder.assigned_to_user,
          has_assigned_to_user: !!sampleOrder.assigned_to_user,
          relations: Object.keys(sampleOrder).filter(key => 
            typeof sampleOrder[key] === 'object' && sampleOrder[key] !== null
          )
        });
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      throw error;
    }
  },
  getItems: async (orderId) => {
    try {
      console.log(`Fetching items for order ${orderId}`);
      const response = await api.get(`/orders/${orderId}/items`);
      console.log(`Items response for order ${orderId}:`, response);
      return response;
    } catch (error) {
      console.error(`Error fetching items for order ${orderId}:`, error);
      throw error;
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/orders', data);
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },
  update: async (id, data) => {
    try {
      // If it's a status update, use the status endpoint
      if (data.status && !data.items) {
        console.log('API update order status request:', {
          id,
          data
        });
        const response = await api.put(`/orders/${id}/status`, data);
        console.log('API update order status response:', response);
        return response;
      }

      // For general updates (with items, etc.), use the main update endpoint
      console.log('API update order request:', {
        id,
        data
      });
      const response = await api.put(`/orders/${id}`, data);
      console.log('API update order response:', response);
      return response;
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await api.delete(`/orders/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  },
  updateStatus: async (id, data) => {
    try {
      console.log('API update order status request:', {
        id,
        data: data instanceof FormData ? 'FormData' : data
      });

      const config = {
        headers: data instanceof FormData ? {
          'Content-Type': 'multipart/form-data'
        } : {
          'Content-Type': 'application/json'
        }
      };

      const response = await api.put(`/orders/${id}/status`, data, config);
      console.log('API update order status response:', response);
      return response;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },
  
  // New methods for role-based status changes
  checkStatusChangePermission: async (orderId) => {
    try {
      const response = await api.get(`/order-status/can-change/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking status change permission:', error);
      throw error;
    }
  },
  
  getAllowedTransitions: async () => {
    try {
      const response = await api.get('/order-status/allowed-transitions');
      return response.data;
    } catch (error) {
      console.error('Error fetching allowed transitions:', error);
      throw error;
    }
  },
  
  updateStatusWithPermission: async (orderId, data) => {
    try {
      console.log('API update order status with permission request:', {
        orderId,
        data: data instanceof FormData ? 'FormData' : data
      });

      const config = {
        headers: data instanceof FormData ? {
          'Content-Type': 'multipart/form-data'
        } : {
          'Content-Type': 'application/json'
        }
      };

      const response = await api.put(`/order-status/permission-update/${orderId}`, data, config);
      console.log('API update order status with permission response:', response);
      return response;
    } catch (error) {
      console.error('Error updating order status with permission:', error);
      throw error;
    }
  }
}

export const productsApi = {
  getAll: async (params) => {
    const response = await api.get('/products', { params });
    return response;
  },
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response;
  },
  create: async (data) => {
    const response = await api.post('/products', data);
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/products/${id}`, data);
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response;
  }
}

export const clientsApi = {
  getAll: async (params) => {
    const response = await api.get('/clients', { params });
    return response;
  },

  getPending: async (params) => {
    const response = await api.get('/clients/pending', { params });
    return response;
  },
  
  getById: async (id) => {
    const response = await api.get(`/clients/${id}`);
    return response;
  },
  
  getClientOrders: async (id) => {
    const response = await api.get(`/clients/${id}/orders`);
    return response;
  },
  
  checkDuplicate: async (data) => {
    const response = await api.post('/clients/check-duplicate', data);
    return response;
  },
  
  create: async (data) => {
    const response = await api.post('/clients', data);
    return response;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/clients/${id}`, data);
    return response;
  },

  approve: async (id) => {
    const response = await api.post(`/clients/${id}/approve`);
    return response;
  },

  reject: async (id, data) => {
    const response = await api.post(`/clients/${id}/reject`, data);
    return response;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/clients/${id}`);
    return response;
  }
}

export const usersApi = {
  async getAll(params = {}) {
    const response = await api.get('/users', { params });
    return response;
  },
  
  async getById(id) {
    const response = await api.get(`/users/${id}`);
    return response;
  },
  
  async getBasicInfo(id) {
    const response = await api.get(`/users/basic/${id}`);
    return response;
  },
  
  async getBasicInfoBatch(userIds) {
    const response = await api.post('/users/basic/batch', { userIds });
    return response;
  },
  
  create: async (data) => {
    const response = await api.post('/users', data);
    return response;
  },
  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response;
  },
  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response;
  }
}

export const authApi = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response;
  },
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response;
  },
  refreshToken: async () => {
    const response = await api.post('/auth/refresh-token');
    return response;
  }
}

export const paymentsApi = {
  async getAll(params) {
    try {
      console.log('Fetching payments with params:', params);
      const response = await api.get('/payments', { 
        params: {
          ...params,
          limit: params.limit || 10
        }
      });
      console.log('Payments response:', response);
      return response;
    } catch (error) {
      console.error('paymentsApi.getAll error:', error);
      throw error;
    }
  },

  async getTotalPayments(period = 'month', start_date, end_date) {
    try {
      const response = await api.get('/payments/total', { 
        params: {
          period,
          start_date,
          end_date
        }
      });
      return response;
    } catch (error) {
      console.error('Error getting total payments:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const response = await api.get(`/payments/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching payment:', error);
      throw error;
    }
  },

  async create(data) {
    try {
      console.log('Creating payment with data:', data);
      const response = await api.post('/payments', data);
      console.log('Create payment response:', response);
      return response;
    } catch (error) {
      console.error('paymentsApi.create error:', error);
      throw error;
    }
  },

  async update(id, data) {
    try {
      console.log('Updating payment:', id, data);
      const response = await api.put(`/payments/${id}`, data);
      console.log('Update payment response:', response);
      return response;
    } catch (error) {
      console.error('paymentsApi.update error:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      console.log('Deleting payment:', id);
      const response = await api.delete(`/payments/${id}`);
      console.log('Delete payment response:', response);
      return response;
    } catch (error) {
      console.error('paymentsApi.delete error:', error);
      throw error;
    }
  },

  async getTypes() {
    try {
      const response = await api.get('/payments/types');
      return response;
    } catch (error) {
      console.error('Error fetching payment types:', error);
      throw error;
    }
  }
};

export const orderStatusApi = {
  getAll: async () => {
    try {
      const response = await api.get('/order-status');
      return response.data;
    } catch (error) {
      console.error('Error fetching order statuses:', error);
      throw error;
    }
  }
};

export const roleApi = {
    getAllRoles: () => api.get('/roles'),
    getRole: (id) => api.get(`/roles/${id}`),
    createRole: (roleData) => api.post('/roles', roleData),
    updateRole: (id, roleData) => api.put(`/roles/${id}`, roleData),
    deleteRole: (id) => api.delete(`/roles/${id}`),
    getAllPermissions: () => api.get('/roles/permissions/all')
};

export const expensesApi = {
  getAll: async (params) => {
    try {
      const response = await api.get('/expenses', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }
  },
  getCategories: async () => {
    try {
      const response = await api.get('/expenses/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching expense categories:', error);
      throw error;
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/expenses', data);
      return response.data;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  },
  update: async (id, data) => {
    try {
      const response = await api.put(`/expenses/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await api.delete(`/expenses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  }
};

export const quotationsApi = {
  getAll: async (params) => {
    try {
      const response = await api.get('/quotations', { params });
      return response;
    } catch (error) {
      console.error('Error fetching quotations:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/quotations/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching quotation:', error);
      throw error;
    }
  },

  generateNumber: async () => {
    try {
      const response = await api.get('/quotations/generate-number');
      return response;
    } catch (error) {
      console.error('Error generating quotation number:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/quotations', data);
      return response;
    } catch (error) {
      console.error('Error creating quotation:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/quotations/${id}`, data);
      return response;
    } catch (error) {
      console.error('Error updating quotation:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/quotations/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting quotation:', error);
      throw error;
    }
  },

  approve: async (id, data) => {
    try {
      const response = await api.post(`/quotations/${id}/approve`, data);
      return response;
    } catch (error) {
      console.error('Error approving quotation:', error);
      throw error;
    }
  },

  clientApprove: async (id, data) => {
    try {
      const response = await api.post(`/quotations/${id}/client-approve`, data);
      return response;
    } catch (error) {
      console.error('Error client approving quotation:', error);
      throw error;
    }
  },

  reject: async (id, data) => {
    try {
      const response = await api.post(`/quotations/${id}/reject`, data);
      return response;
    } catch (error) {
      console.error('Error rejecting quotation:', error);
      throw error;
    }
  }
};

export const productCategoriesApi = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/product-categories', { params });
      return response;
    } catch (error) {
      console.error('Error fetching product categories:', error);
      throw error;
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`/product-categories/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching product category:', error);
      throw error;
    }
  },
  create: async (data) => {
    try {
      const response = await api.post('/product-categories', data);
      return response;
    } catch (error) {
      console.error('Error creating product category:', error);
      throw error;
    }
  },
  update: async (id, data) => {
    try {
      const response = await api.put(`/product-categories/${id}`, data);
      return response;
    } catch (error) {
      console.error('Error updating product category:', error);
      throw error;
    }
  },
  delete: async (id) => {
    try {
      const response = await api.delete(`/product-categories/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting product category:', error);
      throw error;
    }
  }
}; 